import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { ASSETS, PAYHIP_STORE_URL, MEDIA } from "@/lib/config";
import MaskedLines from "@/components/MaskedLines";
import useAutoPlayVideo from "@/hooks/useAutoPlayVideo";

export default function Hero() {
  const [videoReady, setVideoReady] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  // Detect reduced-motion (client-only) — do NOT render/play the video if so
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Robust autoplay: retries on ready, visibility, focus, first user gesture, etc.
  useAutoPlayVideo(videoRef, { enabled: !reducedMotion });

  // --- Parallax scroll on hero content
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  // --- Subtle mouse-driven 3D tilt on the wordmark
  const mvX = useMotionValue(0);
  const mvY = useMotionValue(0);
  const rotX = useSpring(useTransform(mvY, [-1, 1], [4, -4]), { stiffness: 60, damping: 20 });
  const rotY = useSpring(useTransform(mvX, [-1, 1], [-6, 6]), { stiffness: 60, damping: 20 });
  const transX = useSpring(useTransform(mvX, [-1, 1], [-8, 8]), { stiffness: 60, damping: 20 });
  const transY = useSpring(useTransform(mvY, [-1, 1], [-6, 6]), { stiffness: 60, damping: 20 });

  useEffect(() => {
    if (reducedMotion) return;
    const onMove = (e) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      mvX.set((e.clientX / w) * 2 - 1);
      mvY.set((e.clientY / h) * 2 - 1);
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [mvX, mvY, reducedMotion]);

  const handleExplore = (e) => {
    e.preventDefault();
    const el = document.getElementById("about");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid={HERO.root}
      className="relative w-full min-h-[100svh] overflow-hidden bg-[#0a0a0a]"
    >
      {/* Poster fallback — always mounted while video buffers */}
      <motion.div
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
        style={{ y: videoY, scale: videoScale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={MEDIA.heroPoster}
          alt=""
          data-testid="hero-poster"
          className="w-full h-full object-cover"
          draggable={false}
        />
      </motion.div>

      {/* Video background — hidden if reduced-motion is preferred */}
      {!reducedMotion && (
        <motion.video
          ref={videoRef}
          poster={MEDIA.heroPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          controls={false}
          disablePictureInPicture
          aria-hidden="true"
          onCanPlay={() => setVideoReady(true)}
          onLoadedData={() => setVideoReady(true)}
          data-testid="hero-video"
          style={{ y: videoY, scale: videoScale, pointerEvents: "none" }}
          className={`absolute inset-0 w-full h-full object-cover object-center will-change-transform transition-opacity duration-[1600ms] ease-out ${
            videoReady ? "opacity-100" : "opacity-0"
          }`}
        >
          <source src={MEDIA.heroVideoWebm} type="video/webm" />
          <source src={MEDIA.heroVideoMp4} type="video/mp4" />
        </motion.video>
      )}

      {/* Depth layers — vignette + dark blend + top scrim so nav stays legible */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(10,10,10,0.28)_0%,rgba(10,10,10,0.7)_60%,rgba(10,10,10,0.98)_100%)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 via-transparent to-[#0a0a0a]" />

      {/* Cinematic side scrim frames */}
      <div className="absolute inset-y-0 left-0 w-24 md:w-40 bg-gradient-to-r from-[#0a0a0a]/70 to-transparent pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-24 md:w-40 bg-gradient-to-l from-[#0a0a0a]/70 to-transparent pointer-events-none" />

      {/* Grain */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-30 mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.35'/></svg>\")",
        }}
      />

      {/* Chapter meta — top-left */}
      <div className="absolute top-24 md:top-28 left-6 md:left-12 z-20 flex items-center gap-4">
        <motion.span
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.6, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="block h-px w-14 md:w-24 bg-[#c9d3d8]/60 origin-left"
        />
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.9 }}
          className="text-[#c9d3d8] text-[10px] md:text-[11px] tracking-[0.5em] uppercase"
        >
          Chapter 00 — Arrival
        </motion.p>
      </div>

      {/* Vol meta — top-right */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.85, duration: 0.9 }}
        className="absolute top-24 md:top-28 right-6 md:right-12 z-20 text-right"
      >
        <p className="text-[#c9d3d8] text-[10px] md:text-[11px] tracking-[0.5em] uppercase">
          Vol. I / MMXXIV
        </p>
      </motion.div>

      {/* Content — kinetic, masked reveals */}
      <motion.div
        style={{ y: contentY, opacity: contentOpacity }}
        className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-32 md:pb-40 min-h-[100svh] flex flex-col items-center justify-center text-center will-change-transform"
      >
        {/* Small kicker — masked reveal */}
        <MaskedLines
          as="p"
          lines={["A Lifestyle. A Practice."]}
          delay={0.9}
          stagger={0.08}
          duration={0.9}
          className="text-[#c9d3d8] text-[10px] md:text-xs tracking-[0.5em] uppercase mb-14 md:mb-20"
        />

        {/* Signature wordmark — with subtle 3D tilt */}
        <motion.div
          style={{
            rotateX: rotX,
            rotateY: rotY,
            x: transX,
            y: transY,
            transformStyle: "preserve-3d",
            perspective: 1000,
          }}
          className="will-change-transform"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.94, filter: "blur(14px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ delay: 1.15, duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <img
              src={ASSETS.logo}
              alt="VISION"
              className="mx-auto w-[82vw] max-w-[900px] md:max-w-[1050px] h-auto select-none opacity-95"
              draggable={false}
            />
          </motion.div>
          <h1 data-testid={HERO.headline} className="sr-only">
            Live With Vision.
          </h1>
        </motion.div>

        {/* Two-line masked headline beneath */}
        <MaskedLines
          as="h2"
          lines={["Live deliberately.", "Move with intention."]}
          delay={1.55}
          stagger={0.1}
          duration={1.05}
          className="mt-10 md:mt-14 font-serif text-[#e8ecef] text-3xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light"
        />

        {/* Sub */}
        <motion.p
          data-testid={HERO.sub}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.05, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 md:mt-12 text-[#c9d3d8] text-base md:text-lg max-w-xl leading-relaxed font-light"
        >
          A lifestyle built around growth, purpose, wellness, and intentional
          living.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 md:mt-14 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#about"
            onClick={handleExplore}
            data-testid={HERO.exploreBtn}
            className="group relative inline-flex items-center justify-center gap-3 bg-[#e8ecef] text-[#0a0a0a] px-10 py-4 text-[11px] tracking-[0.32em] uppercase overflow-hidden"
          >
            <span className="relative z-10">Explore</span>
            <ArrowRight
              size={14}
              className="relative z-10 transition-transform duration-500 group-hover:translate-x-1"
            />
            <span className="absolute inset-0 bg-[#c9d3d8] translate-y-full group-hover:translate-y-0 transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
          </a>
          <a
            href={PAYHIP_STORE_URL}
            target="_blank"
            rel="noreferrer noopener"
            data-testid={HERO.shopBtn}
            className="group relative inline-flex items-center justify-center gap-3 border border-[#c9d3d8]/40 text-[#e8ecef] px-10 py-4 text-[11px] tracking-[0.32em] uppercase overflow-hidden hover:text-[#0a0a0a] transition-colors duration-500"
          >
            <span className="relative z-10">Shop Collection</span>
            <span className="absolute inset-0 bg-[#e8ecef] translate-y-full group-hover:translate-y-0 transition-transform duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]" />
          </a>
        </motion.div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.6, duration: 1 }}
          className="mt-16 md:mt-20 flex flex-col items-center gap-3 text-[#c9d3d8]/70"
        >
          <span className="text-[10px] tracking-[0.5em] uppercase">Scroll</span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ArrowDown size={14} strokeWidth={1.25} />
          </motion.span>
        </motion.div>
      </motion.div>

      {/* Bottom index strip — anchors hero */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-0 left-0 right-0 z-10 border-t border-[#c9d3d8]/10 bg-gradient-to-b from-transparent to-[#0a0a0a]"
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[#c9d3d8]/75">
          <span>Vol. I — First Light</span>
          <span className="hidden md:inline">A Lifestyle · A Movement · A Practice</span>
          <span>Est. 2024</span>
        </div>
      </motion.div>
    </section>
  );
}
